import { Controller } from "@hotwired/stimulus"

/**
 * Gallery Controller
 *
 * Provides lightbox functionality for image galleries.
 * Creates an overlay dynamically and handles keyboard navigation.
 *
 * Usage:
 *   <div data-controller="gallery" data-gallery-lightbox-value="true">
 *     <a href="/full-image.jpg"
 *        data-gallery-target="image"
 *        data-action="click->gallery#open"
 *        data-index="0">
 *       <img src="/thumbnail.jpg" />
 *     </a>
 *   </div>
 */
export default class extends Controller {
  static targets = ["image"]
  static values = {
    lightbox: { type: Boolean, default: true }
  }

  connect() {
    if (!this.lightboxValue) return

    this.currentIndex = 0
    this.overlay = null
    this.boundKeyHandler = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.close()
  }

  open(event) {
    if (!this.lightboxValue) return

    event.preventDefault()

    const link = event.currentTarget
    this.currentIndex = parseInt(link.dataset.index, 10) || 0

    this.createOverlay()
    this.showImage(this.currentIndex)
    this.overlay.classList.add("is-active")

    document.addEventListener("keydown", this.boundKeyHandler)
    document.body.style.overflow = "hidden"
  }

  close() {
    if (!this.overlay) return

    this.overlay.classList.remove("is-active")
    document.removeEventListener("keydown", this.boundKeyHandler)
    document.body.style.overflow = ""

    setTimeout(() => {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay)
      }
      this.overlay = null
    }, 300)
  }

  prev(event) {
    if (event) event.stopPropagation()
    this.currentIndex = (this.currentIndex - 1 + this.imageTargets.length) % this.imageTargets.length
    this.showImage(this.currentIndex)
  }

  next(event) {
    if (event) event.stopPropagation()
    this.currentIndex = (this.currentIndex + 1) % this.imageTargets.length
    this.showImage(this.currentIndex)
  }

  handleKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.close()
        break
      case "ArrowLeft":
        this.prev()
        break
      case "ArrowRight":
        this.next()
        break
    }
  }

  createOverlay() {
    if (this.overlay) return

    this.overlay = document.createElement("div")
    this.overlay.className = "content-block-gallery__lightbox-overlay"
    this.overlay.setAttribute("role", "dialog")
    this.overlay.setAttribute("aria-modal", "true")
    this.overlay.setAttribute("aria-label", "Image gallery")

    this.overlay.innerHTML = `
      <button type="button"
              class="content-block-gallery__lightbox-close"
              aria-label="Close gallery">
        <span aria-hidden="true">&times;</span>
      </button>
      <button type="button"
              class="content-block-gallery__lightbox-nav content-block-gallery__lightbox-prev"
              aria-label="Previous image">
        <span aria-hidden="true">&#8249;</span>
      </button>
      <div class="content-block-gallery__lightbox-content">
        <img class="content-block-gallery__lightbox-image"
             src=""
             alt="" />
      </div>
      <button type="button"
              class="content-block-gallery__lightbox-nav content-block-gallery__lightbox-next"
              aria-label="Next image">
        <span aria-hidden="true">&#8250;</span>
      </button>
      <div class="content-block-gallery__lightbox-counter">
        <span></span>
      </div>
    `

    // Attach event listeners directly since dynamic elements don't get Stimulus actions
    const closeBtn = this.overlay.querySelector(".content-block-gallery__lightbox-close")
    const prevBtn = this.overlay.querySelector(".content-block-gallery__lightbox-prev")
    const nextBtn = this.overlay.querySelector(".content-block-gallery__lightbox-next")

    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.close()
    })

    prevBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.prev()
    })

    nextBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.next()
    })

    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.close()
      }
    })

    document.body.appendChild(this.overlay)
  }

  showImage(index) {
    if (!this.overlay) return

    const link = this.imageTargets[index]
    if (!link) return

    const src = link.getAttribute("href")
    const img = link.querySelector("img")
    const alt = img ? img.getAttribute("alt") : ""

    const lightboxImage = this.overlay.querySelector(".content-block-gallery__lightbox-image")
    const counter = this.overlay.querySelector(".content-block-gallery__lightbox-counter span")

    if (lightboxImage) {
      lightboxImage.src = src
      lightboxImage.alt = alt
    }

    if (counter) {
      counter.textContent = `${index + 1} / ${this.imageTargets.length}`
    }

    const prevBtn = this.overlay.querySelector(".content-block-gallery__lightbox-prev")
    const nextBtn = this.overlay.querySelector(".content-block-gallery__lightbox-next")

    if (this.imageTargets.length <= 1) {
      if (prevBtn) prevBtn.style.display = "none"
      if (nextBtn) nextBtn.style.display = "none"
    }
  }
}
